import React from "react";
import { TrendingUp, PoundSterling } from "lucide-react";

/**
 * "Will it pay?" — inline salary-progression widget.
 *
 * Reads the authoritative KIS quartiles injected by the Discover Uni overlay:
 *   - outcomes.salary_go15_gbp = { lower, median, upper, sample_size }   (15-mo GO)
 *   - outcomes.salary_leo3_gbp = { lower, median, upper, sample_size }   (3-yr LEO)
 *
 * If either quartile block is missing (non-UK degrees or a UK degree that
 * hasn't been overlaid yet) the widget renders nothing — no placeholders,
 * no fabricated numbers.
 */

const fmtGBP = (n) =>
  typeof n === "number" ? `£${n.toLocaleString("en-GB")}` : "—";

const growth = (from, to) => {
  if (!from || !to) return null;
  return Math.round(((to - from) / from) * 100);
};

export default function SalaryProgressionWidget({ degree }) {
  const go15 = degree?.outcomes?.salary_go15_gbp;
  const leo3 = degree?.outcomes?.salary_leo3_gbp;
  if (!go15?.median && !leo3?.median) return null;

  const g = growth(go15?.median, leo3?.median);

  return (
    <div
      data-testid="salary-progression-widget"
      className="mt-4 mb-6 rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 via-white to-white p-5 shadow-sm"
    >
      <div className="flex items-baseline gap-2 mb-4">
        <PoundSterling className="w-4 h-4 text-emerald-700" aria-hidden />
        <h4 className="text-base font-semibold text-slate-900">
          Will it pay?
        </h4>
        <span className="text-xs text-slate-500 ml-auto">
          Source: HESA / Discover Uni · GBP
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-2">
        {/* 15-month median */}
        <div
          data-testid="salary-go15-panel"
          className="flex flex-col items-start sm:items-center"
        >
          <div className="text-xs uppercase tracking-wider text-slate-500">
            15 months after graduation
          </div>
          <div
            data-testid="salary-go15-median"
            className="mt-1 text-2xl font-semibold tabular-nums text-slate-900"
          >
            {fmtGBP(go15?.median)}
          </div>
          <div className="text-xs text-slate-500 tabular-nums">
            {go15?.lower && go15?.upper
              ? `Range ${fmtGBP(go15.lower)} – ${fmtGBP(go15.upper)}`
              : "\u00A0"}
          </div>
        </div>

        {/* Arrow + delta */}
        <div
          data-testid="salary-progression-delta"
          className="flex items-center justify-center gap-1 py-2 sm:py-0"
        >
          <div className="hidden sm:block h-px w-8 bg-slate-300" aria-hidden />
          <div
            className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
              g == null
                ? "bg-slate-100 text-slate-500"
                : g > 0
                ? "bg-emerald-100 text-emerald-800"
                : "bg-rose-100 text-rose-800"
            }`}
          >
            <TrendingUp className="w-3 h-3" aria-hidden />
            {g == null ? "—" : `${g > 0 ? "+" : ""}${g}%`}
          </div>
          <div className="hidden sm:block h-px w-8 bg-slate-300" aria-hidden />
        </div>

        {/* 3-year median */}
        <div
          data-testid="salary-leo3-panel"
          className="flex flex-col items-start sm:items-end"
        >
          <div className="text-xs uppercase tracking-wider text-slate-500">
            3 years after graduation
          </div>
          <div
            data-testid="salary-leo3-median"
            className="mt-1 text-2xl font-semibold tabular-nums text-slate-900"
          >
            {fmtGBP(leo3?.median)}
          </div>
          <div className="text-xs text-slate-500 tabular-nums">
            {leo3?.lower && leo3?.upper
              ? `Range ${fmtGBP(leo3.lower)} – ${fmtGBP(leo3.upper)}`
              : "\u00A0"}
          </div>
        </div>
      </div>

      <p
        data-testid="salary-progression-caption"
        className="mt-3 text-xs text-slate-500 leading-relaxed"
      >
        Median earnings across{" "}
        <span className="tabular-nums">{go15?.sample_size ?? "—"}</span>{" "}
        graduates 15&nbsp;months out and{" "}
        <span className="tabular-nums">{leo3?.sample_size ?? "—"}</span>{" "}
        graduates 3&nbsp;years out (HESA LEO, this course, this provider).
        Not adjusted for inflation.
      </p>
      {g != null && g < 0 && (
        <p
          data-testid="salary-progression-leo-caveat"
          className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 leading-relaxed"
        >
          <strong>Why the dip?</strong> LEO tracks UK tax records only —
          graduates who moved abroad drop out of the sample, which typically
          pulls high-mobility fields (CS, finance, engineering) downward.
          The 15-month figure is a fairer floor for what you'll actually earn.
        </p>
      )}
    </div>
  );
}
